export interface TableAction {
	id?: number;
	label: string;
	icon?: string;
	action: string;
	color?: string;
	disabled?: boolean;
	command?: (row?: unknown) => void;
	class?: string;
	separator?: boolean;
}

export type TableColumnType =
	| 'text'
	| 'name'
	| 'tag' // Generic tag type - expects field value to be { label: string, color: string }
	| 'actions'
	| 'date';

/** Interface for tag data used with type='tag' columns */
export interface TagData {
	label: string;
	color: 'green' | 'red' | 'blue' | 'gray' | 'yellow';
}

/** Key of a row: the table tracks, expands and selects rows by it */
export type TableRowId = number | string;

/** What every table row has: its id, plus the selection flag the mobile cards set on it */
export interface TableRow {
	id?: TableRowId;
	_selected?: boolean;
}

/**
 * A column's field: a key of the row, or a dotted path into it ('user.name'). Lists also show display
 * fields computed when mapping the rows, so any string is accepted (the row keys are only suggestions).
 */
export type TableColumnField<T> = (keyof T & string) | (string & {});

export interface TableColumn<T = TableRow> {
	field: TableColumnField<T>;
	/** Text shown as is: the caller passes it already translated */
	header: string;
	subField?: string; // Optional second line, smaller and muted (text cells)
	headerIcon?: string; // Optional Lucide icon name for column header
	type?: TableColumnType;
	showAvatar?: boolean; // Only used when type is 'name'
	imageField?: string; // Optional field for image URL when showAvatar is true
	userIdField?: string; // Optional field for user ID (to fetch profile photo from backend)
	extraField?: string; // Optional field for additional information
	sortable?: boolean; // If column is sortable (default true)
	sortField?: string; // Optional: different field name to use for backend sorting (useful when display field is computed)
	transform?: (value: unknown) => unknown; // Function to transform displayed value
	renderAsHtml?: boolean; // If true, the cell value will be rendered as HTML using innerHTML
	tooltipField?: string; // Optional field name containing tooltip text (uses pTooltip)
}

export interface ToggleActionEvent<T = unknown> {
	action: string;
	data: T;
}

/** Page change of the table (paginator or mobile infinite scroll) */
export interface TablePageEvent {
	first: number;
	rows: number;
}

/** A removable filter chip shown above the table */
export interface TableFilterChip {
	key: string;
	label: string;
}

/** A tag cell's value (type 'tag' columns), checked at runtime since cells are read by field path */
export function isTagData(value: unknown): value is TagData {
	return typeof value === 'object' && value !== null && typeof (value as TagData).label === 'string';
}
